// apps/web/src/features/call-center/CallCenterIntakeRoute.tsx
//
// Route element for `/call-center/intake` (#1259, #1398). Mounts as a SIBLING
// of the AppShell route so the internal sidebar/nav never renders here.
//
// Access (#1398 dual-permission gate): three cases decided in order:
//   1. Any other authenticated user (internal/admin — including those whose
//      permission bundle is the 'ALL' sentinel) → redirect to their normal
//      landing. Role is checked FIRST so the ALL-grants sentinel never leaks
//      into the agent branch.
//   2. A `call_center_agent` missing EITHER grant → render a TERMINAL
//      no-access state (NOT a <Navigate>). DANGER: ProtectedRoute pins this
//      role to this path, so any redirect here would bounce straight back and
//      loop forever. We still surface a logout control so a misconfigured
//      external account can sign out.
//   3. Role IS `call_center_agent` AND holds BOTH `callCenterLeads.submit`
//      AND `callCenterLeads.readOwn` → render the self-service own-leads
//      screen.
import { Navigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { hasPermission, useAuthStore } from '../../stores/auth.js';
import { landingForUser } from '../../shell/menu.js';
import { CallCenterSelfServicePage } from './CallCenterSelfServicePage.js';
import { CallCenterIntakeLayout } from './CallCenterIntakeLayout.js';
import { IntakeLogoutButton } from './IntakeLogoutButton.js';

const CALL_CENTER_AGENT_ROLE = 'call_center_agent';

function CallCenterIntakeNoAccess() {
  const { t } = useTranslation('callCenter');
  return (
    <CallCenterIntakeLayout header={<IntakeLogoutButton />}>
      <h1 style={{ fontSize: 'var(--fz-18)', fontWeight: 600, color: 'var(--fg-1)', margin: 0 }}>
        {t('intake.noAccess.title')}
      </h1>
      <p style={{ fontSize: 'var(--fz-13)', color: 'var(--fg-2)', margin: 'var(--s-4) 0 0' }}>
        {t('intake.noAccess.text')}
      </p>
    </CallCenterIntakeLayout>
  );
}

export function CallCenterIntakeRoute() {
  const user = useAuthStore((s) => s.user);
  const permissions = useAuthStore((s) => s.permissions);
  // WHY (#1398 F-001): non-agent redirect is decided FIRST by role so the
  //   'ALL' sentinel never falsely satisfies the agent-branch grant check.
  //   Internal/admin users (including permissions === 'ALL') are sent to their
  //   normal landing before we inspect any grants.
  if (user?.role?.name !== CALL_CENTER_AGENT_ROLE) {
    return <Navigate to={landingForUser(user, permissions)} replace />;
  }
  // WHY (#1398): the self-service screen submits AND reads back own leads, so
  //   it renders only when the agent holds BOTH capability grants.
  const canUse =
    hasPermission(permissions, 'callCenterLeads.submit') &&
    hasPermission(permissions, 'callCenterLeads.readOwn');
  if (canUse) {
    return <CallCenterSelfServicePage />;
  }
  return <CallCenterIntakeNoAccess />;
}
